import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { repeatOf, byTime } from "@/lib/schedule";

const empty = { repeat: "daily", date: "", time: "", program: "", location: "", description: "", published: false };
const sortSchedules = (list) => [...list].sort((a, b) => {
  const ra = repeatOf(a), rb = repeatOf(b);
  if (ra !== rb) return ra === "daily" ? -1 : 1;
  return (a.date || "").localeCompare(b.date || "") || byTime(a, b);
});

export default function SchedulesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/schedules").then((r) => setItems(sortSchedules(r.data))).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ repeat: repeatOf(s), date: s.date || "", time: s.time || "", program: s.program, location: s.location || "", description: s.description || "", published: s.published }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/schedules/${editing.id}`, form);
      else await api.post("/admin/schedules", form);
      toast.success(editing ? "Schedule updated" : "Schedule created");
      setDialogOpen(false); load();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed to save"); } finally { setSaving(false); }
  };

  const togglePublish = async (s) => {
    try { await api.patch(`/admin/schedules/${s.id}/publish`, { published: !s.published }); toast.success(!s.published ? "Published" : "Unpublished"); load(); }
    catch { toast.error("Failed to update"); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/admin/schedules/${deleteId}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
    setDeleteId(null);
  };

  return (
    <div data-testid="admin-schedules-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">Ashram Schedule</p>
          <h1 className="font-display text-3xl font-semibold text-[#194324]">Daily Schedule</h1>
        </div>
        <Button onClick={openAdd} className="bg-[#194324] hover:bg-[#2D623A] text-white rounded-full" data-testid="admin-add-schedule-button">
          <Plus className="h-4 w-4 mr-2" /> Add Schedule
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-[#E2DAD0] overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[#5C7264]"><Clock className="h-10 w-10 mx-auto mb-3 text-[#CBD2CC]" />No schedule entries yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F2EEE5]">
                  <TableHead>Repeats / Date</TableHead><TableHead>Time</TableHead><TableHead>Program</TableHead><TableHead>Location</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                    <TableCell data-testid={`schedule-repeat-${s.id}`}>{repeatOf(s) === "daily" ? <Badge className="border-0 bg-[#E8F0EA] text-[#194324]">Daily</Badge> : s.date}</TableCell>
                    <TableCell className="font-mono text-xs text-[#B38728]">{s.time}</TableCell>
                    <TableCell className="font-medium text-[#194324]">{s.program}</TableCell>
                    <TableCell>{s.location}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={s.published} onCheckedChange={() => togglePublish(s)} data-testid={`schedule-publish-toggle-switch-${s.id}`} />
                        <Badge className={`border-0 ${s.published ? "bg-[#194324] text-white" : "bg-[#F9F3E3] text-[#A8811A]"}`}>{s.published ? "Published" : "Draft"}</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`schedule-edit-${s.id}`}><Pencil className="h-4 w-4 text-[#2D623A]" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(s.id)} data-testid={`schedule-delete-${s.id}`}><Trash2 className="h-4 w-4 text-[#D9531E]" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">{editing ? "Edit Schedule" : "Add Schedule"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Repeats</Label>
              <div className="grid grid-cols-2 gap-2" role="radiogroup">
                {[["daily", "Every day"], ["once", "One date only"]].map(([v, l]) => (
                  <button type="button" key={v} role="radio" aria-checked={form.repeat === v} onClick={() => setForm({ ...form, repeat: v })} className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.repeat === v ? "bg-[#194324] text-white border-[#194324]" : "bg-white text-[#194324] border-[#E2DAD0] hover:border-[#C59B27]"}`} data-testid={`schedule-form-repeat-${v}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {form.repeat === "once" && <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="schedule-form-date" /></div>}
              <div className="space-y-2"><Label>Time</Label><Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="e.g. 5:00 AM" required data-testid="schedule-form-time" /></div>
            </div>
            <div className="space-y-2"><Label>Program</Label><Input value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} placeholder="Brahma Muhurta Meditation" required data-testid="schedule-form-program" /></div>
            <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Main Hall" data-testid="schedule-form-location" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="schedule-form-description" /></div>
            <div className="flex items-center gap-3 bg-[#F9F3E3] rounded-lg px-4 py-3">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="schedule-form-publish" />
              <span className="text-sm text-[#194324]">Publish to public site</span>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-[#194324] hover:bg-[#2D623A] text-white" data-testid="schedule-form-submit-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete schedule entry?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-[#D9531E] hover:bg-[#b8441a]" data-testid="confirm-delete-button">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
